import { BufferGeometry, Color, DoubleSide, Float32BufferAttribute, Mesh, MeshBasicNodeMaterial, Vector3 } from 'three/webgpu'
import { attribute, color, mix, vec3 } from 'three/tsl'
import { HAZE_COLOR, STORM_HAZE_COLOR, lightningUniform, stormUniform } from './Sky.ts'

const SEGMENTS = 384
const ROWS = 5

/** Distant scenery, drawn behind the sea: one mesh, no textures or shadows. */
export class HorizonMountains {
  readonly mesh: Mesh<BufferGeometry, MeshBasicNodeMaterial>

  constructor() {
    const positions: number[] = []
    const colors: number[] = []
    const indices: number[] = []
    const tint = new Color()
    const snow = new Color('#bacbd3')
    const layers = [
      { radius: 6800, height: 850, phase: .7, color: '#829cad' },
      { radius: 6200, height: 570, phase: 2.9, color: '#718fa2' },
      { radius: 5600, height: 300, phase: 4.5, color: '#65879b' },
    ]

    // Integer frequencies close the 360-degree seam. Broad massifs and finer
    // ridges give an irregular skyline without repeating cone-shaped peaks.
    for (const [layer, range] of layers.entries()) {
      const base = positions.length / 3
      const rock = new Color(range.color)
      for (let i = 0; i <= SEGMENTS; i++) {
        const angle = (i % SEGMENTS) / SEGMENTS * Math.PI * 2
        const phase = range.phase
        const massif = Math.pow(.5 + .5 * Math.sin(angle * 3 + phase), 1.5)
        const ridge = .58 + .22 * Math.sin(angle * 11 + phase) + .12 * Math.sin(angle * 23 - phase) + .08 * Math.sin(angle * 47 + phase * 2)
        const height = 35 + range.height * (.18 + .82 * massif) * ridge
        for (let row = 0; row < ROWS; row++) {
          const fraction = row / (ROWS - 1)
          const y = row === 0 ? -1800 : height * (fraction - .25) / .75
          positions.push(Math.sin(angle) * range.radius, y, Math.cos(angle) * range.radius)
          // The foot disappears into sea haze; the crest stays soft and blue.
          const definition = Math.max(0, Math.min(1, y / 150))
          tint.copy(HAZE_COLOR).lerp(rock, definition * (.88 + .12 * Math.sin(angle * 29 + phase)))
          if (layer === 0) {
            const snowLine = 365 + 55 * Math.sin(angle * 19)
            tint.lerp(snow, Math.max(0, Math.min(.7, (y - snowLine) / 140)))
          }
          colors.push(tint.r, tint.g, tint.b)
        }
        if (i < SEGMENTS) {
          for (let row = 0; row < ROWS - 1; row++) {
            const a = base + i * ROWS + row
            indices.push(a, a + ROWS, a + 1, a + 1, a + ROWS, a + ROWS + 1)
          }
        }
      }
    }

    const geometry = new BufferGeometry()
    geometry.setAttribute('position', new Float32BufferAttribute(positions, 3))
    geometry.setAttribute('color', new Float32BufferAttribute(colors, 3))
    geometry.setIndex(indices)
    geometry.computeBoundingSphere()

    const material = new MeshBasicNodeMaterial({ side: DoubleSide, depthWrite: false, depthTest: false, fog: false })
    material.colorNode = mix(attribute('color', 'vec3'), color(STORM_HAZE_COLOR), stormUniform.mul(.94))
      .add(vec3(1, .98, .92).mul(lightningUniform.mul(1.6)))
    this.mesh = new Mesh(geometry, material)
    this.mesh.name = 'horizon-mountains'
    // Far-to-near ridges share a draw; the sky precedes them and the ocean and
    // real islands cover them. Never take part in collision or model LOD.
    this.mesh.renderOrder = -900
    this.mesh.frustumCulled = false
  }

  update(cameraPosition: Vector3): void {
    // Follow horizontally like a sky backdrop, but keep the foot at sea level
    // so raised camera views don't lift the mountains off the water.
    this.mesh.position.set(cameraPosition.x, 0, cameraPosition.z)
  }

  dispose(): void {
    this.mesh.removeFromParent()
    this.mesh.geometry.dispose()
    this.mesh.material.dispose()
  }
}
